import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { api } from "../lib/api";

const navItems = [
  { label: "Home", to: "/" },
  { label: "Events", to: "/events" },
  { label: "About", to: "/about" },
  { label: "Get Involved", to: "/get-involved" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [navPages, setNavPages] = useState([]);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    api.get("/pages").then((r) => setNavPages(r.data.filter((p) => p.show_in_nav))).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-paper/90 backdrop-blur-md border-b border-ink/10" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <Logo className="h-6 md:h-7" />
        </Link>

        <div className="hidden md:flex items-center gap-10">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`font-sans text-sm tracking-wide transition-colors ${
                location.pathname === item.to
                  ? "text-trailride font-semibold"
                  : "text-ink/70 hover:text-ink"
              }`}
            >
              {item.label}
            </Link>
          ))}
          {navPages.map((p) => (
            <Link
              key={p.slug}
              to={`/p/${p.slug}`}
              data-testid={`nav-page-${p.slug}`}
              className={`font-sans text-sm tracking-wide transition-colors ${
                location.pathname === `/p/${p.slug}` ? "text-trailride font-semibold" : "text-ink/70 hover:text-ink"
              }`}
            >
              {p.title}
            </Link>
          ))}
          <Link
            to="/member-login"
            data-testid="nav-member-login"
            className="font-sans text-sm font-medium px-5 py-2 rounded-full border border-ink/20 text-ink hover:border-trailride hover:text-trailride transition-colors"
          >
            Login
          </Link>
        </div>

        <button
          data-testid="nav-hamburger"
          className="md:hidden p-2 -mr-2 text-ink"
          aria-label="Open menu"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-paper border-b border-ink/10 px-6 py-6 flex flex-col gap-5 animate-fade-in"
        >
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`font-sans text-lg ${
                location.pathname === item.to ? "text-trailride font-semibold" : "text-ink"
              }`}
            >
              {item.label}
            </Link>
          ))}
          {navPages.map((p) => (
            <Link
              key={p.slug}
              to={`/p/${p.slug}`}
              data-testid={`mobile-nav-page-${p.slug}`}
              className="font-sans text-lg text-ink"
            >
              {p.title}
            </Link>
          ))}
          <Link
            to="/member-login"
            data-testid="mobile-nav-member-login"
            className="font-sans text-lg font-medium text-ink border-t border-ink/10 pt-5"
          >
            Login
          </Link>
        </div>
      )}
    </header>
  );
};
